import { Check } from 'lucide-react';

/* T6 / #282 — EL CÍRCULO CON PALOMITA, UNA SOLA VEZ EN TODA LA CASA.
 *
 * ── QUÉ ES, Y SOBRE TODO QUÉ NO ES ──────────────────────────────────────────
 *
 * Es un INDICADOR PURO: un `<span aria-hidden>` que se pinta naranja con una
 * palomita cuando algo está marcado. No tiene estado, no tiene manejadores, no
 * es un control y no sabe qué significa estar marcado.
 *
 * LA SEMÁNTICA SE QUEDA EN CADA LLAMADOR, y eso es la decisión de fondo de esta
 * extracción. Los tres sitios que lo usan tienen semánticas DISTINTAS y las tres
 * son correctas donde están:
 *
 *   · `TopicPickerModal` — un `<button aria-pressed>` que envuelve la tarjeta
 *     entera. Es un alternador, no una casilla de formulario.
 *   · Las dos casillas del banco (`GroupBankTemasPage`) — un
 *     `<input type="checkbox">` NATIVO. Son casillas de verdad: eligen qué se
 *     genera y qué se publica.
 *
 * Meter aquí un `role="checkbox"` con `aria-checked` habría hecho dos daños a la
 * vez: le quitaría al modal su `aria-pressed` (rompiendo el render byte-idéntico
 * que esta tanda promete) y sustituiría en las casillas una semántica NATIVA
 * —con su foco, su tecla Espacio y su participación en el formulario, todo
 * gratis— por una imitación escrita a mano. Un `role` tecleado nunca es mejor
 * que el elemento que ya lo tiene.
 *
 * ── LOS DOS TONOS ───────────────────────────────────────────────────────────
 *
 * Apagado: gris oscuro sobre transparente. Encendido: relleno naranja de marca,
 * borde del mismo naranja y la palomita blanca. Son los valores EXACTOS que el
 * `TopicPickerModal` ya tenía: esta extracción no cambia un píxel, y hay un
 * snapshot que lo fija.
 *
 * (Los nombres de esas clases NO se escriben en este comentario a propósito —
 * regla 5.6 de la casa: Tailwind escanea con una expresión regular y no
 * distingue un comentario del código, así que nombrarlas aquí las PUBLICA en la
 * hoja compilada aunque nadie las use.)
 *
 * ── EL APAGADO SE ACLARÓ, Y ES UN SOLO TONO PARA LAS TRES PANTALLAS ────────
 *
 * EL DEFECTO (tandas 33 y 33b, firmas #302 y #303): el círculo desmarcado no se
 * veía. Contra los fondos donde vive de verdad, el gris que traía daba entre
 * 1.29:1 y 1.53:1 de contraste — la regla no-textual pide 3:1, así que estaba a
 * menos de la mitad del piso en TODAS. «Ni parece que se puede palomear.»
 *
 * LA 33 LO ARREGLÓ SÓLO EN EL CARRIL DEL PROFESOR, con una prop, porque su firma
 * dejaba congelado el selector del alumno. La 33b miró las dos pantallas juntas
 * y firmó lo contrario: era el mismo defecto en las dos, y que en el selector se
 * viera «menos mal» no lo hacía legible. Así que el tono claro pasó a ser EL
 * default y la prop se retiró: los tres llamadores quieren lo mismo, y una prop
 * sin llamador se lee como «aquí hay dos tonos posibles» cuando ya no los hay.
 *
 * EL TONO SE ELIGIÓ MIDIENDO, no a ojo: es el más OSCURO que pasa 3:1 contra los
 * CINCO fondos reales (la fila del selector y su realce; la tarjeta de tema del
 * banco y el suyo; y el panel del modal de publicar). El más oscuro y no el más
 * claro porque el apagado no debe competir con el naranja del marcado, que es el
 * que tiene que ganar la mirada. La tabla de ratios y el cálculo viven en
 * `CirculoPalomita.t6.test.js`, que recalcula los números y falla solo si
 * alguien retoca el tono o un fondo.
 */
export default function CirculoPalomita({ marcado, className = '' }) {
  return (
    <span
      aria-hidden="true"
      className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${
        marcado
          ? 'bg-[#FF6B00] border-[#FF6B00]'
          : 'border-[#777] bg-transparent'
      }${className ? ` ${className}` : ''}`}
    >
      {marcado && <Check className="w-3 h-3 text-white" />}
    </span>
  );
}
